import * as React from "react"
import { shallowEqual } from "@digits-shared/helpers/objectHelper"
import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector"

export type StoreApi<TState> = {
  getState: () => TState
  setState: (
    updater: TState | Partial<TState> | ((prev: TState) => TState | Partial<TState>)
  ) => void
  subscribe: (listener: () => void) => () => void
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

/**
 * Creates a minimal external store with subscribe/getState/setState for use with
 * React's external store hooks.
 *
 * The store supports partial updates when both the current state and next value
 * are plain objects; otherwise it replaces the state.
 *
 * Subscribers are notified only when the state reference changes to avoid
 * re-renders on identical updates.
 *
 * @example
 * const store = createStore({ count: 0 })
 */
export function createStore<TState>(initialState: TState): StoreApi<TState> {
  let state = initialState
  const listeners = new Set<() => void>()

  const getState = () => state

  const setState: StoreApi<TState>["setState"] = (updater) => {
    const nextValue =
      typeof updater === "function"
        ? (updater as (prev: TState) => TState | Partial<TState>)(state)
        : updater
    const nextState =
      isPlainObject(state) && isPlainObject(nextValue)
        ? ({ ...state, ...nextValue } as TState)
        : (nextValue as TState)

    if (Object.is(nextState, state)) return
    state = nextState
    listeners.forEach((listener) => listener())
  }

  const subscribe: StoreApi<TState>["subscribe"] = (listener) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  return { getState, setState, subscribe }
}

/**
 * Creates a React context + provider scoped to a tree with selector hooks.
 *
 * Each Provider instance owns its own store, enabling tree-local state isolation.
 * `useSelector` subscribes to a derived slice, while `useSelectorShallow` keeps
 * the previous selection when shallow-equal to avoid re-renders.
 *
 * @example
 * const { Provider, useSelector } = createScopedStore<State>()
 */
export function createScopedStore<TState>() {
  const Context = React.createContext<StoreApi<TState> | null>(null)

  const Provider: React.FC<React.PropsWithChildren<{ initialState: TState }>> = ({
    initialState,
    children,
  }) => {
    const storeRef = React.useRef<StoreApi<TState> | null>(null)
    if (!storeRef.current) {
      storeRef.current = createStore(initialState)
    }

    return <Context.Provider value={storeRef.current}>{children}</Context.Provider>
  }

  const useStore = () => {
    const store = React.useContext(Context)
    if (!store) {
      throw new Error("Scoped store provider is missing")
    }
    return store
  }

  const useSelector = <TSelected,>(selector: (state: TState) => TSelected) => {
    const store = useStore()
    return React.useSyncExternalStore(
      store.subscribe,
      () => selector(store.getState()),
      () => selector(store.getState())
    )
  }

  const useSelectorShallow = <TSelected,>(selector: (state: TState) => TSelected) => {
    const store = useStore()

    return useSyncExternalStoreWithSelector(
      store.subscribe,
      store.getState,
      store.getState,
      selector,
      shallowEqual
    )
  }

  return { Context, Provider, useSelector, useSelectorShallow }
}
